import { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

type Source = { src: string; width: number; type?: string };
type GalleryItem = { src: string; width?: number | null; height?: number | null; sources?: Source[] };

export default function Gallery() {
  const [images, setImages] = useState<string[]>([]);
  const [items, setItems] = useState<GalleryItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(-1);
  const [page, setPage] = useState(0);
  const PER_PAGE = 8;
  const close = useCallback(() => setSelected(-1), []);
  const prev = useCallback(() => setSelected((i) => (i - 1 + (items?.length || images.length)) % (items?.length || images.length)), [items, images.length]);
  const next = useCallback(() => setSelected((i) => (i + 1) % (items?.length || images.length)), [items, images.length]);

  // Keyboard navigation when lightbox is open
  useEffect(() => {
    if (selected < 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, close, prev, next]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        // Prefer static manifest so it works on static hosting
        let res = await fetch('/gallery.json', { cache: 'no-store' });
        if (res.ok) {
          const j = await res.json();
          if (
            mounted &&
            ((Array.isArray(j.images) && j.images.length) || (Array.isArray(j.items) && j.items.length))
          ) {
            setImages(j.images || []);
            if (Array.isArray(j.items) && j.items.length) setItems(j.items);
            setLoading(false);
            return;
          }
        }
      } catch {}
      try {
        // Fallback to API if available
        let res2 = await fetch('/api/gallery', { cache: 'no-store' });
        if (res2.ok) {
          const j2 = await res2.json();
          if (mounted) {
            setImages(j2.images || []);
            if (Array.isArray(j2.items)) setItems(j2.items);
          }
        }
      } catch {}
      if (mounted) setLoading(false);
    }
    load();
    return () => {
      mounted = false;
    };
  }, []);
  const MDiv = motion.div as any;
  const MImg = motion.img as any;
  const pageBtnRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  const total = (items && items.length ? items.length : images.length) || 0;
  const pageCount = Math.max(1, Math.ceil(total / PER_PAGE));
  const start = Math.min(page, pageCount - 1) * PER_PAGE;
  const end = Math.min(total, start + PER_PAGE);
  const onPrevPage = () => setPage((p) => Math.max(0, p - 1));
  const onNextPage = () => setPage((p) => Math.min(pageCount - 1, p + 1));
  const onGoto = (p: number) => setPage(() => Math.min(Math.max(0, p), pageCount - 1));
  const pages = (() => {
    const list: Array<number | string> = [];
    if (pageCount <= 9) {
      for (let i = 0; i < pageCount; i++) list.push(i);
      return list;
    }
    const first = 0;
    const last = pageCount - 1;
    const windowStart = Math.max(first + 1, page - 2);
    const windowEnd = Math.min(last - 1, page + 2);
    list.push(first);
    if (windowStart > first + 1) list.push('…');
    for (let i = windowStart; i <= windowEnd; i++) list.push(i);
    if (windowEnd < last - 1) list.push('…');
    list.push(last);
    return list;
  })();

  // Keyboard navigation on individual page buttons
  const onPageKey = (current: number) => (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (pageCount <= 1) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      onGoto(current - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      onGoto(current + 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      onGoto(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      onGoto(pageCount - 1);
    }
  };

  // After page changes, move focus to the active page button
  useEffect(() => {
    const el = pageBtnRefs.current[page];
    if (el) el.focus();
  }, [page]);

  return (
    <main id="main">
      <section className="section section-alt">
        <div className="container">
          <h1 className="section-title">Gallery</h1>
          {loading && <p className="fine">Loading photos.</p>}
          <div
            className="cards"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 220px))',
              justifyContent: 'center',
            }}
          >
            {(items && items.length ? items.map((it) => it.src) : images)
              .slice(start, end)
              .map((p, idx) => (
              <MDiv
                className="card"
                key={p}
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 200, damping: 18 }}
                onClick={() => setSelected(start + idx)}
                style={{ cursor: 'pointer' }}
              >
                <MImg
                  className="gallery-thumb"
                  src={process.env.PUBLIC_URL + p}
                  srcSet={items?.[start + idx]?.sources?.map((s) => `${process.env.PUBLIC_URL + s.src} ${s.width}w`).join(', ')}
                  sizes="(max-width: 600px) 100vw, 33vw"
                  alt="Gym"
                  width={items?.[start + idx]?.width || undefined}
                  height={items?.[start + idx]?.height || undefined}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              </MDiv>
            ))}
            {!loading && images.length === 0 && (
              <p className="fine">No photos found in the gallery yet.</p>
            )}
          </div>

          {/* Pagination Controls */}
          {total > PER_PAGE && (
            <nav
              className="pagination"
              aria-label="Gallery pagination"
              style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', marginTop: 16, flexWrap: 'wrap' }}
            >
              <button className="btn btn-outline" onClick={onPrevPage} disabled={page <= 0} aria-label="Previous page">Prev</button>
              {pages.map((p, i) =>
                typeof p === 'number' ? (
                  <button
                    key={`p${p}`}
                    className={`btn ${p === page ? 'btn-accent' : 'btn-outline'}`}
                    aria-label={`Page ${p + 1}`}
                    aria-current={p === page}
                    ref={(el) => {
                      pageBtnRefs.current[p] = el;
                    }}
                    onKeyDown={onPageKey(p)}
                    onClick={() => onGoto(p)}
                  >
                    {p + 1}
                  </button>
                ) : (
                  <span key={`e${i}`} className="fine" aria-hidden="true" style={{ padding: '6px 4px' }}>
                    {p}
                  </span>
                ),
              )}
              <button className="btn btn-outline" onClick={onNextPage} disabled={page >= pageCount - 1} aria-label="Next page">Next</button>
            </nav>
          )}
        </div>
      </section>

      <AnimatePresence>
        {selected >= 0 && (items?.[selected]?.src || images[selected]) && (
          <MDiv
            className="lightbox-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
          >
            <MDiv
              className="lightbox-panel"
              role="dialog"
              aria-modal="true"
              aria-label="Gallery image"
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
            >
              <img
                className="lightbox-image"
                src={process.env.PUBLIC_URL + (items?.[selected]?.src || images[selected])}
                srcSet={items?.[selected]?.sources?.map((s) => `${process.env.PUBLIC_URL + s.src} ${s.width}w`).join(', ')}
                sizes="(max-width: 800px) 100vw, 80vw"
                alt="Gallery large"
                width={items?.[selected]?.width || undefined}
                height={items?.[selected]?.height || undefined}
              />
              <button className="lightbox-btn prev" aria-label="Previous" onClick={prev} />
              <button className="lightbox-btn next" aria-label="Next" onClick={next} />
              <button className="lightbox-close" aria-label="Close" onClick={close}>
                x
              </button>
            </MDiv>
          </MDiv>
        )}
      </AnimatePresence>
    </main>
  );
}
